import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Spinner, Modal, Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faEdit, faTrash, faSyncAlt, faCalendarAlt,
  faTasks, faCheckCircle, faExclamationTriangle, faSave,
  faBuilding, faMapMarkerAlt, faPhone, faEnvelope, faIndustry,
  faSearch, faFilter, faTimes, faChevronLeft, faChevronRight,
  faAnglesLeft, faAnglesRight, faClipboardList, faEraser,
  faEye
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import Dexie from 'dexie';
import RapportMissionModal from './RapportMissionModal';    // création/modification
import RapportMissionModal2 from './RapportMissionModal2';  // visualisation / PDF

// ============================================================
// 1. CACHE DEXIE (24h) + UTILITAIRE RETRY
// ============================================================
const db = new Dexie('MissionsCacheDB');
db.version(1).stores({
  missions: 'key, data, timestamp',
  entreprises: 'key, data, timestamp'
});
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCached(store, key) {
  try {
    const entry = await db[store].get(key);
    if (!entry) return null;
    if (Date.now() - entry.timestamp > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
}
async function setCached(store, key, data) {
  await db[store].put({ key, data, timestamp: Date.now() });
}
async function clearAllCache() {
  await db.missions.clear();
  await db.entreprises.clear();
}

// fetch avec retry automatique sur 429
const fetchWithRetry = async (url, options = {}, retries = 3, baseDelay = 1000) => {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url, options);
      if (response.status === 429 && attempt < retries) {
        const retryAfter = response.headers.get('Retry-After');
        const delay = retryAfter ? parseInt(retryAfter, 10) * 1000 : baseDelay * Math.pow(2, attempt);
        console.warn(`⏳ 429 détecté, nouvelle tentative dans ${delay}ms`);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.json();
    } catch (error) {
      if (attempt === retries) throw error;
      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

const API_MISSION_URL = `${API_CONFIG2.apiUrl}mission/api_mission.php`;
const API_ENTREPRISE_URL = `${API_CONFIG2.apiUrl}entreprises/api_entreprise.php`;
const API_RAPPORT = `${API_CONFIG2.apiUrl}mission/api_rapport_mission2.php`; // pour fetch_by_mission

const ManageMissions = ({ entrepriseId: initialEntrepriseId, userId = null }) => {
  // --- États ---
  const [entreprises, setEntreprises] = useState([]);
  const [selectedEntrepriseId, setSelectedEntrepriseId] = useState(initialEntrepriseId || null);
  const [missions, setMissions] = useState([]);
  const [filteredMissions, setFilteredMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [dataSource, setDataSource] = useState('cache');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);

  // Filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [filterEntreprise, setFilterEntreprise] = useState('');

  // Modales CRUD mission
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ id_entreprise: '', mission: '', dates: '' });
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Modales Rapport
  const [showRapportModal, setShowRapportModal] = useState(false);
  const [showRapportViewModal, setShowRapportViewModal] = useState(false);
  const [editingRapportId, setEditingRapportId] = useState(null);
  const [viewRapportId, setViewRapportId] = useState(null);
  const [selectedMissionId, setSelectedMissionId] = useState(null);
  const [selectedEntrepriseIdForRapport, setSelectedEntrepriseIdForRapport] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const [toast, setToast] = useState(null);
  const abortControllerRef = useRef(null);
  const isMounted = useRef(true);

  // --- Toast ---
  const showToast = (msg, variant = 'success') => {
    setToast({ msg, variant });
    setTimeout(() => setToast(null), 5000);
  };

  // --- Chargement des entreprises ---
  const fetchEntreprises = useCallback(async (forceRefresh = false) => {
    if (initialEntrepriseId) return [];
    const cacheKey = 'entreprises_list';
    try {
      if (!forceRefresh) {
        const cached = await getCached('entreprises', cacheKey);
        if (cached) {
          setEntreprises(cached);
          setDataSource('cache');
          return cached;
        }
      }
      const url = `${API_ENTREPRISE_URL}?action=list&unique=${Date.now()}`;
      const data = await fetchWithRetry(url);
      if (data.status === 'success') {
        setEntreprises(data.data);
        await setCached('entreprises', cacheKey, data.data);
        setDataSource('live');
        return data.data;
      }
      throw new Error(data.message || 'Erreur');
    } catch (err) {
      setError('Impossible de charger la liste des entreprises : ' + err.message);
      const cached = await getCached('entreprises', cacheKey);
      if (cached) {
        setEntreprises(cached);
        setDataSource('cache (expiré)');
        return cached;
      }
      return [];
    }
  }, [initialEntrepriseId]);

  // --- Chargement des missions ---
  const fetchMissions = useCallback(async (id_entreprise, forceRefresh = false) => {
    if (!id_entreprise) {
      setMissions([]);
      setFilteredMissions([]);
      setLoading(false);
      return;
    }
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (forceRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);

    const cacheKey = `missions_${id_entreprise}`;

    try {
      if (!forceRefresh) {
        const cached = await getCached('missions', cacheKey);
        if (cached) {
          setMissions(cached);
          setDataSource('cache');
          setLoading(false);
          return;
        }
      }
      const url = `${API_MISSION_URL}?action=list&id_entreprise=${id_entreprise}&unique=${Date.now()}`;
      const data = await fetchWithRetry(url, { signal: controller.signal });
      if (data.status === 'success') {
        setMissions(data.data);
        await setCached('missions', cacheKey, data.data);
        setDataSource('live');
      } else {
        throw new Error(data.message || 'Erreur');
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      setError(err.message);
      const cached = await getCached('missions', cacheKey);
      if (cached) {
        setMissions(cached);
        setDataSource('cache (expiré)');
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  // --- Vidage du cache ---
  const handleClearCache = useCallback(async () => {
    try {
      await clearAllCache();
      showToast('Cache vidé avec succès', 'success');
      if (selectedEntrepriseId) {
        await fetchMissions(selectedEntrepriseId, true);
      } else {
        await fetchEntreprises(true);
      }
    } catch (err) {
      showToast('Erreur lors du vidage du cache', 'danger');
    }
  }, [selectedEntrepriseId, fetchMissions, fetchEntreprises, showToast]);

  // --- Filtrage et recherche ---
  const applyFilters = useCallback(() => {
    let result = [...missions];
    if (filterEntreprise) {
      result = result.filter(m => m.id_entreprise === parseInt(filterEntreprise));
    }
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter(m =>
        m.mission?.toLowerCase().includes(term) ||
        m.nomEntreprise?.toLowerCase().includes(term) ||
        m.adresse?.toLowerCase().includes(term) ||
        m.telephone?.includes(term)
      );
    }
    if (filterDate) {
      result = result.filter(m => m.dates === filterDate);
    }
    result.sort((a, b) => new Date(b.dates) - new Date(a.dates));
    setFilteredMissions(result);
    setCurrentPage(1);
  }, [missions, searchTerm, filterDate, filterEntreprise]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // --- Pagination ---
  const totalPages = Math.ceil(filteredMissions.length / itemsPerPage);
  const paginatedMissions = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredMissions.slice(start, start + itemsPerPage);
  }, [filteredMissions, currentPage, itemsPerPage]);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  // --- Initialisation ---
  useEffect(() => {
    isMounted.current = true;
    const init = async () => {
      if (initialEntrepriseId) {
        setSelectedEntrepriseId(initialEntrepriseId);
        await fetchMissions(initialEntrepriseId);
        setLoading(false);
      } else {
        const list = await fetchEntreprises();
        if (list && list.length > 0) {
          const id = list[0]?.id;
          setSelectedEntrepriseId(id);
          if (id) await fetchMissions(id);
        } else {
          setLoading(false);
        }
      }
    };
    init();
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []); // eslint-disable-line

  // --- Changement d'entreprise ---
  const handleEntrepriseChange = (e) => {
    const id = parseInt(e.target.value);
    setSelectedEntrepriseId(id);
    setFilterEntreprise('');
    fetchMissions(id);
  };

  // --- CRUD Mission ---
  const openCreateModal = () => {
    setEditing(null);
    const defaultId = selectedEntrepriseId || (entreprises.length > 0 ? entreprises[0].id : '');
    setForm({ id_entreprise: defaultId, mission: '', dates: '' });
    setShowModal(true);
  };

  const openEditModal = (mission) => {
    setEditing(mission);
    setForm({
      id_entreprise: mission.id_entreprise,
      mission: mission.mission,
      dates: mission.dates
    });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.mission.trim() || !form.dates || !form.id_entreprise) {
      showToast('Veuillez remplir tous les champs', 'warning');
      return;
    }
    setSubmitting(true);
    const payload = {
      id_entreprise: parseInt(form.id_entreprise),
      mission: form.mission.trim(),
      dates: form.dates
    };
    try {
      let url, options;
      if (editing) {
        payload.id = editing.id;
        url = `${API_MISSION_URL}?action=update`;
        options = { method: 'PUT', body: JSON.stringify(payload) };
      } else {
        url = `${API_MISSION_URL}?action=create`;
        options = { method: 'POST', body: JSON.stringify(payload) };
      }
      const data = await fetchWithRetry(url, {
        ...options,
        headers: { 'Content-Type': 'application/json' }
      });
      if (data.status !== 'success') throw new Error(data.message || 'Erreur');
      showToast(editing ? 'Mission mise à jour ✓' : 'Mission créée ✓', 'success');
      setShowModal(false);
      fetchMissions(selectedEntrepriseId, true);
    } catch (err) {
      showToast(err.message, 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = (mission) => {
    setDeleteTarget(mission);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(deleteTarget.id);
    try {
      const data = await fetchWithRetry(`${API_MISSION_URL}?action=delete&id=${deleteTarget.id}`, { method: 'DELETE' });
      if (data.status !== 'success') throw new Error(data.message || 'Erreur');
      showToast('Mission supprimée ✓', 'success');
      setShowDeleteModal(false);
      setDeleteTarget(null);
      fetchMissions(selectedEntrepriseId, true);
    } catch (err) {
      showToast(err.message, 'danger');
    } finally {
      setDeleting(null);
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilterDate('');
    setFilterEntreprise('');
  };

  // --- Gestion des modales Rapport ---
  // Ouvre le modal de création/modification (RapportMissionModal)
  const openRapportModal = (mission) => {
    setSelectedMissionId(mission.id);
    setSelectedEntrepriseIdForRapport(mission.id_entreprise);
    setEditingRapportId(mission.rapport_id || null);
    setShowRapportModal(true);
  };

  const closeRapportModal = () => {
    setShowRapportModal(false);
    setSelectedMissionId(null);
    setSelectedEntrepriseIdForRapport(null);
    setEditingRapportId(null);
  };

  // Ouvre le modal de visualisation (RapportMissionModal2) après avoir récupéré le rapport par missionId
  const handleViewRapport = async (missionId) => {
    setViewLoading(true);
    try {
      const url = `${API_RAPPORT}?action=fetch_by_mission&missionId=${missionId}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.status === 'success' && data.data) {
        // Un rapport existe
        setViewRapportId(data.data.id);
      } else {
        // Aucun rapport trouvé
        setViewRapportId(null);
      }
      setShowRapportViewModal(true);
    } catch (err) {
      showToast('Erreur lors de la récupération du rapport', 'danger');
      setViewRapportId(null);
      setShowRapportViewModal(true); // ouvre le modal avec null pour afficher le message
    } finally {
      setViewLoading(false);
    }
  };

  const closeRapportView = () => {
    setShowRapportViewModal(false);
    setViewRapportId(null);
  };

  const handleRapportSuccess = (data) => {
    showToast('Rapport enregistré avec succès ✓', 'success');
    fetchMissions(selectedEntrepriseId, true);
  };

  // --- Rendu squelette de chargement ---
  const renderSkeleton = () => (
    <div className="cards-grid skeleton-grid">
      {[...Array(itemsPerPage)].map((_, i) => (
        <div key={i} className="skeleton-card">
          <div className="skeleton-line skeleton-title"></div>
          <div className="skeleton-line skeleton-text"></div>
          <div className="skeleton-line skeleton-text short"></div>
          <div className="skeleton-line skeleton-text"></div>
          <div className="skeleton-actions">
            <div className="skeleton-btn"></div>
            <div className="skeleton-btn"></div>
            <div className="skeleton-btn"></div>
          </div>
        </div>
      ))}
    </div>
  );

  // --- États de chargement ---
  if (loading && !missions.length && !entreprises.length) {
    return (
      <div className="missions-wrapper loading-state">
        <div className="glass-card header-skeleton">
          <div className="skeleton-title" style={{ width: '40%' }}></div>
          <div className="skeleton-btn" style={{ width: '100px' }}></div>
        </div>
        {renderSkeleton()}
      </div>
    );
  }

  return (
    <>
      <style>{`
        /* ============================================================
           DESIGN ULTRA MODERNE – MISSIONS EN CARTES AVEC ANIMATIONS
           ============================================================ */
        :root {
          --accent-blue: #3b82f6;
          --accent-dark: #1d4ed8;
          --bg-dark: #0a0f1f;
          --bg-card: rgba(20, 40, 80, 0.55);
          --text-light: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius-card: 18px;
          --shadow-card: 0 12px 40px rgba(0, 0, 0, 0.4);
          --transition-smooth: 0.3s cubic-bezier(0.2, 0, 0, 1);
        }

        .missions-wrapper {
          background: radial-gradient(ellipse at 20% 30%, #132a4a, var(--bg-dark));
          padding: clamp(0.8rem, 2vw, 1.5rem);
          border-radius: 24px;
          min-height: 70vh;
          margin-top: 0.5rem;
          position: relative;
          overflow: hidden;
        }

        .missions-wrapper::before {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at 30% 70%, rgba(59, 130, 246, 0.06), transparent 60%);
          animation: pulseGlow 8s ease-in-out infinite alternate;
          pointer-events: none;
          z-index: 0;
        }
        @keyframes pulseGlow {
          0% { opacity: 0.3; transform: scale(1); }
          100% { opacity: 0.8; transform: scale(1.1); }
        }
        .missions-wrapper > * {
          position: relative;
          z-index: 1;
        }

        .glass-card {
          background: var(--bg-card);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid rgba(59, 130, 246, 0.12);
          border-radius: var(--radius-card);
          padding: 1.2rem 1.5rem;
          box-shadow: var(--shadow-card);
          transition: border-color 0.3s, box-shadow 0.3s;
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.5);
        }

        .badge-source {
          font-size: 0.65rem;
          padding: 0.2rem 0.6rem;
          border-radius: 30px;
          background: rgba(59, 130, 246, 0.12);
          color: #4f8cff;
          border: 1px solid rgba(59, 130, 246, 0.08);
          white-space: nowrap;
          margin-left: 0.4rem;
          font-weight: 400;
        }
        .badge-source.live {
          background: rgba(46, 204, 113, 0.12);
          color: #2ecc71;
          border-color: rgba(46, 204, 113, 0.08);
        }
        .badge-source.warning {
          background: rgba(245, 158, 11, 0.12);
          color: #f59e0b;
          border-color: rgba(245, 158, 11, 0.08);
        }

        /* --- Header --- */
        .header-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.8rem;
          margin-bottom: 1.2rem;
        }
        .header-section h5 {
          font-weight: 700;
          color: var(--text-light);
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: clamp(1rem, 2.5vw, 1.25rem);
        }
        .header-section h5 .badge-count {
          font-size: 0.7rem;
          font-weight: 400;
          background: rgba(79, 140, 255, 0.15);
          color: #4f8cff;
          padding: 0.1rem 0.7rem;
          border-radius: 30px;
          animation: fadeScale 0.4s ease;
        }
        @keyframes fadeScale {
          from { opacity: 0; transform: scale(0.8); }
          to { opacity: 1; transform: scale(1); }
        }

        /* --- Boutons --- */
        .btn-glass {
          padding: 8px 18px;
          border-radius: 40px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-light);
          transition: all var(--transition-smooth);
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 500;
          cursor: pointer;
          font-size: clamp(0.75rem, 1.1vw, 0.9rem);
          position: relative;
          overflow: hidden;
        }
        .btn-glass::after {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at var(--x, 50%) var(--y, 50%), rgba(255,255,255,0.06), transparent 60%);
          opacity: 0;
          transition: opacity 0.4s;
          pointer-events: none;
        }
        .btn-glass:hover::after {
          opacity: 1;
        }
        .btn-glass:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px) scale(1.02);
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
        }
        .btn-glass:active:not(:disabled) {
          transform: scale(0.96);
        }
        .btn-glass:disabled {
          opacity: 0.4;
          pointer-events: none;
        }

        .btn-glass.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.25);
        }
        .btn-glass.primary:hover:not(:disabled) {
          background: var(--accent-dark);
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4);
        }

        .btn-glass.danger {
          background: rgba(239, 68, 68, 0.12);
          color: #f87171;
          border: 1px solid rgba(239, 68, 68, 0.2);
        }
        .btn-glass.danger:hover:not(:disabled) {
          background: rgba(239, 68, 68, 0.25);
        }

        .btn-glass.outline {
          background: transparent;
          border: 1px solid rgba(255,255,255,0.08);
        }
        .btn-glass.outline:hover:not(:disabled) {
          background: rgba(255,255,255,0.04);
        }

        .btn-glass.icon-only {
          padding: 6px 10px;
          min-width: 36px;
          justify-content: center;
        }
        .btn-glass.sm {
          padding: 4px 12px;
          font-size: 0.75rem;
        }
        .btn-glass .label {
          display: inline;
        }
        @media (max-width: 480px) {
          .btn-glass .label {
            display: none;
          }
          .btn-glass.icon-only {
            min-width: 40px;
            padding: 6px 12px;
          }
        }

        /* --- Filtres --- */
        .filters-bar {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.6rem;
          margin-bottom: 1.2rem;
        }
        .filters-bar .search-wrapper {
          flex: 1;
          min-width: 160px;
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 40px;
          padding: 0.2rem 0.6rem 0.2rem 1rem;
          transition: 0.3s;
        }
        .filters-bar .search-wrapper:focus-within {
          border-color: var(--accent-blue);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08);
        }
        .filters-bar .search-wrapper input {
          background: transparent;
          border: none;
          color: var(--text-light);
          outline: none;
          flex: 1;
          padding: 0.5rem 0;
          font-size: clamp(0.8rem, 1vw, 0.9rem);
        }
        .filters-bar .search-wrapper input::placeholder {
          color: var(--text-muted);
        }
        .filters-bar .search-wrapper .clear-btn {
          background: transparent;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          padding: 0 6px;
          transition: color 0.2s;
        }
        .filters-bar .search-wrapper .clear-btn:hover {
          color: var(--text-light);
        }

        .filters-bar .filter-select {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 40px;
          color: var(--text-light);
          padding: 0.35rem 0.8rem;
          outline: none;
          font-size: clamp(0.75rem, 0.9vw, 0.85rem);
          min-width: 100px;
          cursor: pointer;
          transition: 0.3s;
        }
        .filters-bar .filter-select option {
          background: #0b1428;
          color: var(--text-light);
        }
        .filters-bar .filter-select:focus {
          border-color: var(--accent-blue);
        }

        .filters-bar .reset-btn {
          background: transparent;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          padding: 0.2rem 0.6rem;
          border-radius: 30px;
          transition: 0.2s;
          font-size: 0.8rem;
        }
        .filters-bar .reset-btn:hover {
          background: rgba(255,255,255,0.04);
          color: var(--text-light);
        }

        @media (max-width: 768px) {
          .filters-bar {
            flex-direction: column;
            align-items: stretch;
          }
          .filters-bar .search-wrapper {
            width: 100%;
            min-width: unset;
          }
          .filters-bar .filter-select {
            width: 100%;
            min-width: unset;
          }
        }

        /* --- Grille de cartes --- */
        .cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(min(100%, 320px), 1fr));
          gap: clamp(0.8rem, 1.5vw, 1.2rem);
          margin-top: 0.5rem;
        }

        .mission-card {
          animation: fadeUp 0.6s ease both;
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(4px);
          border: 1px solid rgba(255, 255, 255, 0.06);
          border-radius: 16px;
          padding: clamp(0.8rem, 1.2vw, 1.2rem);
          transition: all var(--transition-smooth);
          display: flex;
          flex-direction: column;
          position: relative;
          overflow: hidden;
        }
        .mission-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent, var(--accent-blue), transparent);
          opacity: 0;
          transition: opacity 0.6s;
        }
        .mission-card:hover::before {
          opacity: 1;
        }
        .mission-card:hover {
          background: rgba(255, 255, 255, 0.07);
          border-color: rgba(59, 130, 246, 0.2);
          transform: translateY(-6px) scale(1.01);
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.3);
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .mission-card:nth-child(1) { animation-delay: 0.05s; }
        .mission-card:nth-child(2) { animation-delay: 0.10s; }
        .mission-card:nth-child(3) { animation-delay: 0.15s; }
        .mission-card:nth-child(4) { animation-delay: 0.20s; }
        .mission-card:nth-child(5) { animation-delay: 0.25s; }
        .mission-card:nth-child(6) { animation-delay: 0.30s; }
        .mission-card:nth-child(7) { animation-delay: 0.35s; }
        .mission-card:nth-child(8) { animation-delay: 0.40s; }
        .mission-card:nth-child(9) { animation-delay: 0.45s; }
        .mission-card:nth-child(10) { animation-delay: 0.50s; }
        .mission-card:nth-child(11) { animation-delay: 0.55s; }
        .mission-card:nth-child(12) { animation-delay: 0.60s; }

        .mission-card .card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 0.4rem;
          flex-wrap: wrap;
          gap: 0.3rem;
        }
        .mission-card .card-header .mission-id {
          font-size: 0.6rem;
          color: var(--text-muted);
          background: rgba(255,255,255,0.04);
          padding: 0.1rem 0.6rem;
          border-radius: 30px;
          font-weight: 500;
        }
        .mission-card .card-header .entreprise-badge {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 0.65rem;
          color: #4f8cff;
          background: rgba(79, 140, 255, 0.08);
          padding: 0.1rem 0.6rem;
          border-radius: 30px;
        }

        .mission-card .mission-text {
          font-weight: 500;
          color: var(--text-light);
          font-size: clamp(0.9rem, 1.1vw, 1rem);
          line-height: 1.4;
          margin-bottom: 0.2rem;
          flex: 1;
        }

        .mission-card .mission-date {
          font-size: 0.75rem;
          color: var(--text-secondary);
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 0.3rem;
        }

        .mission-card .entreprise-details {
          margin-top: 0.3rem;
          font-size: clamp(0.65rem, 0.8vw, 0.75rem);
          color: var(--text-secondary);
          display: flex;
          flex-direction: column;
          gap: 2px;
          background: rgba(255,255,255,0.02);
          padding: 0.3rem 0.5rem;
          border-radius: 10px;
          border-left: 2px solid rgba(59, 130, 246, 0.15);
        }
        .mission-card .entreprise-details .detail-row {
          display: flex;
          align-items: center;
          gap: 5px;
        }
        .mission-card .entreprise-details .detail-row .icon {
          width: 12px;
          color: var(--text-muted);
        }
        .mission-card .entreprise-details .detail-row .value {
          color: #cbd5e1;
          word-break: break-word;
        }
        .mission-card .entreprise-details .detail-row .value.strong {
          color: var(--text-light);
          font-weight: 500;
        }

        .mission-card .card-actions {
          display: flex;
          justify-content: flex-end;
          gap: 4px;
          margin-top: 0.5rem;
          padding-top: 0.5rem;
          border-top: 1px solid rgba(255,255,255,0.04);
        }

        /* --- Skeleton loading --- */
        .skeleton-grid {
          gap: 1rem;
        }
        .skeleton-card {
          background: rgba(255,255,255,0.03);
          border-radius: 16px;
          padding: 1rem;
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
          border: 1px solid rgba(255,255,255,0.04);
          animation: pulseSkeleton 1.5s ease-in-out infinite;
        }
        @keyframes pulseSkeleton {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .skeleton-line {
          height: 12px;
          border-radius: 6px;
          background: rgba(255,255,255,0.06);
          width: 100%;
        }
        .skeleton-line.skeleton-title {
          height: 18px;
          width: 60%;
        }
        .skeleton-line.skeleton-text {
          height: 12px;
        }
        .skeleton-line.skeleton-text.short {
          width: 40%;
        }
        .skeleton-actions {
          display: flex;
          gap: 0.5rem;
          justify-content: flex-end;
          margin-top: 0.4rem;
        }
        .skeleton-btn {
          width: 32px;
          height: 32px;
          border-radius: 30px;
          background: rgba(255,255,255,0.06);
        }

        .header-skeleton {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        /* --- Pagination --- */
        .pagination-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.8rem;
          margin-top: 1.2rem;
          padding-top: 0.8rem;
          border-top: 1px solid rgba(255,255,255,0.04);
        }
        .pagination-bar .info {
          color: var(--text-secondary);
          font-size: clamp(0.7rem, 0.9vw, 0.85rem);
        }
        .pagination-bar .info strong {
          color: var(--text-light);
        }
        .pagination-controls {
          display: flex;
          gap: 3px;
          align-items: center;
          flex-wrap: wrap;
        }
        .pagination-controls button {
          padding: 4px 10px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-light);
          transition: all var(--transition-smooth);
          font-size: clamp(0.7rem, 0.8vw, 0.8rem);
          cursor: pointer;
          min-width: 30px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .pagination-controls button:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: scale(1.05);
        }
        .pagination-controls button:disabled {
          opacity: 0.3;
          cursor: not-allowed;
          transform: none;
        }
        .pagination-controls button.active {
          background: var(--accent-blue);
          color: #fff;
          border-color: var(--accent-blue);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.15);
        }
        .pagination-controls .ellipsis {
          color: var(--text-muted);
          padding: 0 3px;
        }

        .pagination-controls .per-page {
          display: flex;
          align-items: center;
          gap: 4px;
          margin-left: 0.3rem;
        }
        .pagination-controls .per-page span {
          font-size: 0.7rem;
          color: var(--text-secondary);
        }
        .pagination-controls .per-page select {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          color: var(--text-light);
          padding: 0.1rem 0.4rem;
          outline: none;
          font-size: 0.7rem;
          cursor: pointer;
          transition: 0.2s;
        }
        .pagination-controls .per-page select:focus {
          border-color: var(--accent-blue);
        }

        @media (max-width: 768px) {
          .pagination-bar {
            flex-direction: column;
            align-items: center;
          }
          .pagination-controls {
            justify-content: center;
          }
          .pagination-controls .per-page {
            margin-left: 0;
          }
        }
        @media (max-width: 480px) {
          .pagination-controls button {
            min-width: 28px;
            padding: 3px 6px;
            font-size: 0.65rem;
          }
          .pagination-controls .per-page select {
            font-size: 0.65rem;
            padding: 0.05rem 0.3rem;
          }
        }

        /* --- États vides et erreurs --- */
        .empty-state {
          text-align: center;
          padding: 2rem 0;
          color: var(--text-secondary);
        }
        .empty-state .icon {
          font-size: 2.5rem;
          color: #5e6f94;
          opacity: 0.3;
          margin-bottom: 0.5rem;
          animation: float 3s ease-in-out infinite;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }

        .alert-glass {
          background: rgba(255,255,255,0.03);
          backdrop-filter: blur(8px);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 14px;
          color: var(--text-light);
          padding: 10px 16px;
        }
        .alert-glass-danger {
          border-color: rgba(239, 68, 68, 0.2);
          background: rgba(239, 68, 68, 0.04);
          color: #f87171;
        }

        /* --- Toast --- */
        .toast-container {
          position: fixed;
          top: 16px;
          right: 16px;
          z-index: 9999;
          max-width: 400px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .toast-item {
          background: rgba(10, 20, 45, 0.9);
          backdrop-filter: blur(24px);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: 16px;
          padding: 12px 16px;
          color: var(--text-light);
          display: flex;
          align-items: center;
          gap: 12px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.5);
          animation: slideIn 0.4s ease;
        }
        .toast-item.success { border-left: 4px solid var(--accent-blue); }
        .toast-item.warning { border-left: 4px solid #f59e0b; }
        .toast-item.danger { border-left: 4px solid #ef4444; }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(60px) scale(0.95); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }
        .btn-close-custom {
          background: transparent;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          font-size: 1.2rem;
          padding: 0 4px;
          margin-left: auto;
          transition: color 0.2s;
        }
        .btn-close-custom:hover { color: var(--text-light); }

        /* --- Modal améliorée avec animations --- */
        .modal-glass .modal-content {
          background: rgba(10, 20, 45, 0.92);
          backdrop-filter: blur(30px);
          border: 1px solid rgba(59, 130, 246, 0.12);
          border-radius: 28px;
          color: var(--text-light);
          animation: modalFadeIn 0.4s ease;
        }
        @keyframes modalFadeIn {
          from { opacity: 0; transform: scale(0.95) translateY(20px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .modal-glass .modal-header {
          border-bottom: 1px solid rgba(59, 130, 246, 0.06);
          padding: 1rem 1.5rem;
        }
        .modal-glass .modal-footer {
          border-top: 1px solid rgba(59, 130, 246, 0.06);
          padding: 0.8rem 1.5rem;
          flex-wrap: wrap;
        }
        .modal-glass .modal-body {
          padding: 1.5rem;
        }
        .modal-glass .form-control,
        .modal-glass .form-select {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          color: var(--text-light);
          border-radius: 12px;
          transition: 0.3s;
        }
        .modal-glass .form-control:focus,
        .modal-glass .form-select:focus {
          border-color: var(--accent-blue);
          box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
        }
        .modal-glass .form-control::placeholder {
          color: var(--text-muted);
        }
        .modal-glass .btn-close {
          filter: invert(1) brightness(200%);
          opacity: 0.6;
          transition: opacity 0.2s;
        }
        .modal-glass .btn-close:hover {
          opacity: 1;
        }

        @media (max-width: 576px) {
          .modal-glass .modal-content {
            border-radius: 20px;
          }
          .modal-glass .modal-header {
            padding: 0.8rem 1rem;
          }
          .modal-glass .modal-body {
            padding: 1rem;
          }
          .modal-glass .modal-footer {
            flex-direction: column-reverse;
          }
          .modal-glass .modal-footer button {
            width: 100%;
            justify-content: center;
          }
        }

        /* --- Sélecteur d'entreprise --- */
        .entreprise-selector {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          background: rgba(255,255,255,0.03);
          padding: 0.1rem 0.4rem 0.1rem 0.8rem;
          border-radius: 40px;
          border: 1px solid rgba(255,255,255,0.06);
          transition: 0.3s;
        }
        .entreprise-selector:hover {
          border-color: rgba(255,255,255,0.12);
        }
        .entreprise-selector .form-select {
          background: transparent;
          border: none;
          color: var(--text-light);
          outline: none;
          padding: 0.2rem 0.4rem;
          min-width: 100px;
          font-size: clamp(0.75rem, 0.9vw, 0.85rem);
          cursor: pointer;
        }
        .entreprise-selector .form-select option {
          background: #0b1428;
        }
        @media (max-width: 480px) {
          .entreprise-selector .form-select {
            min-width: 80px;
            padding: 0.1rem 0.3rem;
          }
        }

        .loading-state .glass-card {
          animation: pulseGlow 2s ease-in-out infinite;
        }

        /* ============================================================
           STYLES POUR LE MODAL RAPPORT (design blanc et bleu)
           ============================================================ */
        .rapport-modal .modal-content {
          border-radius: 0.5rem;
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
          background: #fff;
          color: #333;
        }
        .rapport-modal .modal-header {
          background-color: #f8f9fa;
          border-bottom: 1px solid #dee2e6;
        }
        .rapport-modal .modal-title {
          font-weight: 600;
          color: #007bff;
        }
        .rapport-modal .modal-body {
          padding: 0;
        }
        #rapport-content {
          padding: 2rem;
          background: white;
          color: #333;
        }
        #rapport-content h2 {
          font-weight: 700;
          color: #007bff;
        }
        #rapport-content h5 {
          margin-top: 1.5rem;
          color: #007bff;
          border-bottom: 1px solid #e9ecef;
          padding-bottom: 0.3rem;
        }
        #rapport-content p {
          margin-bottom: 0.4rem;
        }
        #rapport-content .row {
          display: flex;
          flex-wrap: wrap;
          margin: 0 -0.5rem;
        }
        #rapport-content .col-md-6 {
          flex: 0 0 50%;
          max-width: 50%;
          padding: 0 0.5rem;
        }
        @media (max-width: 768px) {
          #rapport-content .col-md-6 {
            flex: 0 0 100%;
            max-width: 100%;
          }
        }
        #rapport-content .border-bottom {
          border-bottom: 1px solid #e9ecef;
        }
        #rapport-content .text-primary {
          color: #007bff !important;
        }
        #rapport-content .text-muted {
          color: #6c757d !important;
        }
        #rapport-content .fw-bold {
          font-weight: 700;
        }
        .rapport-modal .modal-footer {
          background-color: #f8f9fa;
          border-top: 1px solid #dee2e6;
        }
        .rapport-modal .btn-primary {
          background-color: #007bff;
          border-color: #007bff;
        }
        .rapport-modal .btn-primary:hover {
          background-color: #0069d9;
          border-color: #0062cc;
        }
        .rapport-modal .btn-secondary {
          background-color: #6c757d;
          border-color: #6c757d;
        }
        .rapport-modal .btn-secondary:hover {
          background-color: #5a6268;
          border-color: #545b62;
        }
        .rapport-modal .btn i {
          margin-right: 0.3rem;
        }
      `}</style>

      <div className="missions-wrapper">
        {/* --- Header --- */}
        <div className="header-section">
          <h5>
            <FontAwesomeIcon icon={faTasks} className="text-primary me-2" />
            Missions
            <span className="badge-count">{filteredMissions.length}</span>
            <span className={`badge-source ${dataSource === 'live' ? 'live' : dataSource === 'cache (expiré)' ? 'warning' : ''}`}>
              {dataSource === 'cache' ? '📦 Cache' :
               dataSource === 'cache (expiré)' ? '⚠️ Cache expiré' :
               dataSource === 'live' ? '🔄 Live' : ''}
            </span>
          </h5>
          <div className="d-flex gap-2 flex-wrap align-items-center">
            {!initialEntrepriseId && (
              <div className="entreprise-selector">
                <FontAwesomeIcon icon={faBuilding} className="text-secondary" style={{ fontSize: '0.8rem' }} />
                <Form.Select
                  value={selectedEntrepriseId || ''}
                  onChange={handleEntrepriseChange}
                  disabled={loading || entreprises.length === 0}
                >
                  {entreprises.map(ent => (
                    <option key={ent.id} value={ent.id}>{ent.nomEntreprise}</option>
                  ))}
                </Form.Select>
              </div>
            )}
            <button
              className="btn-glass outline sm"
              onClick={() => fetchMissions(selectedEntrepriseId, true)}
              disabled={refreshing || !selectedEntrepriseId}
              title="Actualiser"
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={refreshing} />
              <span className="label">Actualiser</span>
            </button>
            <button
              className="btn-glass outline sm"
              onClick={handleClearCache}
              disabled={refreshing || loading}
              title="Vider le cache"
            >
              <FontAwesomeIcon icon={faEraser} />
              <span className="label">Vider le cache</span>
            </button>
            <button
              className="btn-glass primary sm"
              onClick={openCreateModal}
              disabled={!selectedEntrepriseId}
            >
              <FontAwesomeIcon icon={faPlus} />
              <span className="label">Ajouter</span>
            </button>
          </div>
        </div>

        {/* --- Filtres --- */}
        <div className="filters-bar">
          <div className="search-wrapper">
            <FontAwesomeIcon icon={faSearch} className="text-muted" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="clear-btn" onClick={() => setSearchTerm('')}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>
          <Form.Control
            type="date"
            className="filter-select"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            placeholder="Date"
          />
          {!initialEntrepriseId && entreprises.length > 0 && (
            <Form.Select
              className="filter-select"
              value={filterEntreprise}
              onChange={(e) => setFilterEntreprise(e.target.value)}
            >
              <option value="">Toutes les entreprises</option>
              {entreprises.map(ent => (
                <option key={ent.id} value={ent.id}>{ent.nomEntreprise}</option>
              ))}
            </Form.Select>
          )}
          {(searchTerm || filterDate || filterEntreprise) && (
            <button className="reset-btn" onClick={resetFilters}>
              <FontAwesomeIcon icon={faTimes} className="me-1" /> Réinitialiser
            </button>
          )}
        </div>

        {/* --- Erreur --- */}
        {error && (
          <div className="alert-glass alert-glass-danger mb-3">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            {error}
          </div>
        )}

        {/* --- Grille des missions --- */}
        {loading && !missions.length ? (
          renderSkeleton()
        ) : filteredMissions.length === 0 ? (
          <div className="empty-state">
            <FontAwesomeIcon icon={faTasks} className="icon" />
            <p>Aucune mission trouvée.</p>
            <p className="small text-muted">Essayez de modifier vos filtres.</p>
          </div>
        ) : (
          <>
            <div className="cards-grid">
              {paginatedMissions.map(m => (
                <div key={m.id} className="mission-card">
                  <div className="card-header">
                    <span className="mission-id">#{m.id}</span>
                    <span className="entreprise-badge">
                      <FontAwesomeIcon icon={faBuilding} size="xs" />
                      {m.nomEntreprise}
                    </span>
                  </div>

                  <div className="mission-text">{m.mission}</div>

                  <div className="mission-date">
                    <FontAwesomeIcon icon={faCalendarAlt} className="text-muted" />
                    {new Date(m.dates).toLocaleDateString('fr-FR', {
                      day: '2-digit', month: 'short', year: 'numeric'
                    })}
                  </div>

                  <div className="entreprise-details">
                    <div className="detail-row">
                      <span className="icon"><FontAwesomeIcon icon={faMapMarkerAlt} /></span>
                      <span className="value">{m.adresse}</span>
                    </div>
                    <div className="detail-row">
                      <span className="icon"><FontAwesomeIcon icon={faPhone} /></span>
                      <span className="value">{m.telephone}</span>
                    </div>
                    {m.email && (
                      <div className="detail-row">
                        <span className="icon"><FontAwesomeIcon icon={faEnvelope} /></span>
                        <span className="value">{m.email}</span>
                      </div>
                    )}
                    {m.secteur_activite && (
                      <div className="detail-row">
                        <span className="icon"><FontAwesomeIcon icon={faIndustry} /></span>
                        <span className="value">{m.secteur_activite}</span>
                      </div>
                    )}
                  </div>

                  <div className="card-actions">
                    <button
                      className="btn-glass icon-only sm"
                      onClick={() => openEditModal(m)}
                      title="Modifier la mission"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>

                    {m.rapport_id ? (
                      <>
                        <button
                          className="btn-glass icon-only sm"
                          onClick={() => openRapportModal(m)}
                          title="Modifier le rapport"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
  className="btn-glass icon-only sm primary"
  onClick={() => {
    setSelectedMissionId(m.id);
    setShowRapportViewModal(true);
  }}
  title="Voir le rapport"
>
  <FontAwesomeIcon icon={faEye} />
</button>
                      </>
                    ) : (
                      <>
                        <button
                          className="btn-glass icon-only sm primary"
                          onClick={() => openRapportModal(m)}
                          title="Ajouter un rapport"
                        >
                          <FontAwesomeIcon icon={faClipboardList} />
                        </button>
                        {/* Bouton "Voir le rapport" toujours présent (peut être vide) */}
                       <button
  className="btn-glass icon-only sm primary"
  onClick={() => {
    setSelectedMissionId(m.id);
    setShowRapportViewModal(true);
  }}
  title="Voir le rapport"
>
  <FontAwesomeIcon icon={faEye} />
</button>
                      </>
                    )}

                    <button
                      className="btn-glass icon-only sm danger"
                      onClick={() => confirmDelete(m)}
                      disabled={deleting === m.id}
                      title="Supprimer"
                    >
                      {deleting === m.id ? <Spinner as="span" animation="border" size="sm" /> : <FontAwesomeIcon icon={faTrash} />}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* --- Pagination --- */}
            {totalPages > 1 && (
              <div className="pagination-bar">
                <div className="info">
                  <strong>{filteredMissions.length}</strong> mission(s) — Page <strong>{currentPage}</strong> / {totalPages}
                </div>
                <div className="pagination-controls">
                  <button onClick={() => goToPage(1)} disabled={currentPage === 1} title="Première">
                    <FontAwesomeIcon icon={faAnglesLeft} />
                  </button>
                  <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} title="Précédente">
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>

                  {[...Array(totalPages)].map((_, idx) => {
                    const p = idx + 1;
                    if (
                      p === 1 ||
                      p === totalPages ||
                      (p >= currentPage - 1 && p <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={p}
                          className={p === currentPage ? 'active' : ''}
                          onClick={() => goToPage(p)}
                        >
                          {p}
                        </button>
                      );
                    }
                    if (p === currentPage - 2 || p === currentPage + 2) {
                      return <span key={p} className="ellipsis">…</span>;
                    }
                    return null;
                  })}

                  <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} title="Suivante">
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                  <button onClick={() => goToPage(totalPages)} disabled={currentPage === totalPages} title="Dernière">
                    <FontAwesomeIcon icon={faAnglesRight} />
                  </button>

                  <div className="per-page">
                    <span>Par page</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                    >
                      <option value="4">4</option>
                      <option value="6">6</option>
                      <option value="12">12</option>
                      <option value="24">24</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* --- MODAL CRÉATION / MODIFICATION MISSION --- */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered backdrop="static" className="modal-glass">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={editing ? faEdit : faPlus} className="text-primary me-2" />
            {editing ? 'Modifier la mission' : 'Ajouter une mission'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Entreprise <span className="text-danger">*</span></Form.Label>
              <Form.Select
                name="id_entreprise"
                value={form.id_entreprise || ''}
                onChange={handleChange}
                required
                disabled={submitting}
              >
                <option value="">Sélectionner une entreprise</option>
                {entreprises.map(ent => (
                  <option className="bg-dark" key={ent.id} value={ent.id}>{ent.nomEntreprise}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Mission <span className="text-danger">*</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="mission"
                value={form.mission}
                onChange={handleChange}
                placeholder="Décrivez la mission..."
                required
                disabled={submitting}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Date <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="date"
                name="dates"
                value={form.dates}
                onChange={handleChange}
                required
                disabled={submitting}
              />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2 flex-wrap">
              <Button variant="secondary" className="btn-glass outline" onClick={() => setShowModal(false)} disabled={submitting}>
                Annuler
              </Button>
              <Button variant="primary" type="submit" className="btn-glass primary" disabled={submitting}>
                {submitting ? <Spinner as="span" animation="border" size="sm" className="me-1" /> : <FontAwesomeIcon icon={faSave} className="me-1" />}
                {editing ? 'Mettre à jour' : 'Ajouter'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* --- MODAL SUPPRESSION MISSION --- */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered backdrop="static" className="modal-glass" size="sm">
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">Confirmer la suppression</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">Supprimer définitivement cette mission ?</p>
          {deleteTarget && (
            <p className="text-secondary small">{deleteTarget.mission}</p>
          )}
          <small className="text-muted">Cette action est irréversible.</small>
        </Modal.Body>
        <Modal.Footer className="d-flex gap-2 flex-wrap">
          <Button variant="secondary" className="btn-glass outline" onClick={() => setShowDeleteModal(false)} disabled={deleting !== null}>
            Annuler
          </Button>
          <Button variant="danger" className="btn-glass danger" onClick={handleDelete} disabled={deleting !== null}>
            {deleting !== null ? <Spinner as="span" animation="border" size="sm" /> : 'Supprimer'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* --- MODAL RAPPORT – CRÉATION / MODIFICATION --- */}
      <RapportMissionModal
        show={showRapportModal}
        onHide={closeRapportModal}
        onSuccess={handleRapportSuccess}
        entrepriseId={selectedEntrepriseIdForRapport}
        missionId={selectedMissionId}
        rapportId={editingRapportId}
        userId={userId}
      />

      {/* --- MODAL RAPPORT – VISUALISATION / PDF --- */}
      <RapportMissionModal2
  show={showRapportViewModal}
  handleClose={closeRapportView}
  missionId={selectedMissionId}  // ← on passe l'id de la mission
/>

      {/* --- TOAST --- */}
      {toast && (
        <div className="toast-container">
          <div className={`toast-item ${toast.variant}`}>
            <FontAwesomeIcon
              icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
              className={toast.variant === 'success' ? 'text-primary' : 'text-danger'}
            />
            <span>{toast.msg}</span>
            <button className="btn-close-custom" onClick={() => setToast(null)}>✕</button>
          </div>
        </div>
      )}
    </>
  );
};

export default ManageMissions;